import {
  Alert,
  Column,
  Columns,
  Stack,
  TextLink,
  Text,
} from "@origin-digital/ods-core";
import * as React from "react";
import { FormValues } from "./types";
import { useFormikContext } from "formik";
import FormContext from "src/context/FormContext";
import { format } from "date-fns";
import { isUnavailableDateForFuture } from "./helper";
import { apiClient } from "src/api/apiService";
import ReadOnlyCanRetailBlock from "./Internet/ReadOnlyCanRetailBlock";
import ReadOnlyPreinstallNBNBlock from "./Internet/ReadOnlyPreinstallNBNBlock";
import RequestorInfoInternet from "./RequestorInfoInternet";
import { ConnectionTypeEnum, EICStatusEnum } from "../Premises";

const ReadOnlyInternetBlock = () => {
  const { values } = useFormikContext<FormValues>();
  const {
    formState: { internetService },
  } = React.useContext(FormContext);
  const { setDisplayingSnackbar } = React.useContext(FormContext);

  const handleResend = async () => {
    try {
      const resendRes = await apiClient.post(
        "/internet/customer-communication",
        {
          premise_id: values.id,
          request_id_being_lodged: internetService?.requestId,
        }
      );
      if (resendRes.status === 200) {
        // Show success snackbar
        setDisplayingSnackbar({
          message: "Notification re-sent.",
          tone: "positive",
        });
      } else {
        setDisplayingSnackbar({
          message: "Failed to re-send notification.",
          tone: "caution",
        });
      }
    } catch (error) {
      setDisplayingSnackbar({
        message: "Failed to re-send notification.",
        tone: "caution",
      });
    }
  };

  const commSentDateTime = internetService?.commSentDateTime
    ? format(new Date(internetService?.commSentDateTime), "dd/MM/yyyy")
    : "";

  const showNotice = React.useMemo(() => {
    if (
      internetService?.handoverDate &&
      isUnavailableDateForFuture(new Date(internetService?.handoverDate))
    )
      return false;

    if (internetService?.connectionType === ConnectionTypeEnum.PRE_INSTALL_NBN)
      return (
        internetService?.rawRequestStatus === "COMPLETE" &&
        (values.internetEicStatus === EICStatusEnum.EIC_COMM_SENT ||
          values.internetEicStatus === EICStatusEnum.EIC_COMM_REMINDER_SENT)
      );
    if (
      internetService?.connectionType === ConnectionTypeEnum.ADDRESS_NOT_FOUND
    ) {
      return false; // no notice for ADDRESS_NOT_FOUND and readonly block, because its a cancelled request
    }
    return true; // return true for CAN_RETAIL as long as handover date is not in past
  }, [
    internetService?.connectionType,
    internetService?.handoverDate,
    internetService?.rawRequestStatus,
    values.internetEicStatus,
  ]);

  return (
    <Stack
      space={{
        xs: "xlarge",
        md: "xxlarge",
      }}
    >
      <Stack
        space={{
          xs: "xlarge",
          md: "xlarge",
        }}
      >
        {showNotice && (
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            <Column width="2/3">
              <Alert>
                <Text>
                  Email about our Internet plans sent to account holder on{" "}
                  {commSentDateTime}.{" "}
                  <TextLink href="#internet-tab" onClick={handleResend}>
                    Resend?
                  </TextLink>
                </Text>
              </Alert>
            </Column>
            <Column width="1/3" />
          </Columns>
        )}
        {internetService?.connectionType === ConnectionTypeEnum.CAN_RETAIL && (
          <ReadOnlyCanRetailBlock />
        )}
        {(internetService?.connectionType ===
          ConnectionTypeEnum.PRE_INSTALL_NBN ||
          internetService?.connectionType ===
            ConnectionTypeEnum.ADDRESS_NOT_FOUND) && (
          <ReadOnlyPreinstallNBNBlock />
        )}
      </Stack>
      <RequestorInfoInternet
        requesterName={values.internetRequesterName || " "}
        submittedDateTime={values.internetSubmittedDateTime || ""}
        closeByName={values.internetCloseByName || " "}
        closeActionType="pre-installation completed"
        closeDateTime={values.internetCloseByDate || ""}
        cancelByName={values.internetCancelByName || " "}
        cancelActionType="pre-installation cancelled"
        cancelDateTime={values.internetCancelByDate || ""}
      />
    </Stack>
  );
};

export default ReadOnlyInternetBlock;
